import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '../ui';
import { verdictLabel } from '../../utils/formatting';
import {
    ANOMALY_GROUPS, VERDICT_CONFIG, countGroups, countVerdicts, verdictTooltip,
} from '../../utils/anomalySignals';
import { countQualifyingQuotes, countRetarget } from '../../utils/inventoryView';
import { signalThresholds } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { BoltIcon, TargetIcon, TaskIcon } from './icons';

const GROUP_TONES = { critical: 'red', warning: 'amber', positive: 'sage' };

/**
 * Anomaly signal groups (counted over ALL units, as today) and the
 * opportunity toggles on one row, plus a "Refine signal" row per expanded
 * group. Group click: always toggles the expansion, and selects every
 * present verdict of the group unless all are already selected (then it
 * deselects them) — today's AnomalyStrip behaviour.
 */
function SignalsRow({ page, allData, shownCount }) {
    const thresholds = signalThresholds(useDealershipSettings());
    const {
        filters, anomalyFilter, handleAnomalyVerdictToggle, handleAnomalyGroupToggle, handleAnomalyClearSignals,
        toggleQualifyingQuote, toggleRetargeting, taskFilterActive, openTaskCount, toggleTaskFilter,
    } = page;
    const [expanded, setExpanded] = useState(() => new Set());

    // A selection made elsewhere (Needs attention, ?signal=, saved prefs, the
    // Signals pill) opens the groups it touches, so it is visible (QA B6).
    // Changes made in this row keep today's expand / collapse behaviour.
    const ownChange = useRef(false);
    const own = (fn) => (...args) => { ownChange.current = true; fn(...args); };
    useEffect(() => {
        if (ownChange.current) {
            ownChange.current = false;
            return;
        }
        const touched = ANOMALY_GROUPS.filter((g) => g.verdicts.some((v) => anomalyFilter.has(v))).map((g) => g.key);
        if (!touched.length) return;
        setExpanded((prev) => (touched.every((k) => prev.has(k)) ? prev : new Set([...prev, ...touched])));
    }, [anomalyFilter]);

    const verdictCounts = useMemo(() => countVerdicts(allData), [allData]);
    const groupCounts = useMemo(() => countGroups(verdictCounts), [verdictCounts]);
    const qqCount = useMemo(() => countQualifyingQuotes(allData), [allData]);
    const retargetCount = useMemo(() => countRetarget(allData), [allData]);
    const anySignals = ANOMALY_GROUPS.some((g) => groupCounts[g.key] > 0);

    const clickGroup = own((g) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
            return next;
        });
        const present = g.verdicts.filter((v) => verdictCounts[v] > 0);
        const allActive = present.every((v) => anomalyFilter.has(v));
        handleAnomalyGroupToggle(present, !allActive);
    });

    const activeText = `Active — showing ${shownCount.toLocaleString('en-US')} units. Click to turn off.`;

    return (
        <>
            <div className="inv2-signals" role="group" aria-label="Signals and opportunities">
                {anySignals && (
                    <>
                        <span className="inv2-sg-label" aria-hidden="true">Signals</span>
                        {ANOMALY_GROUPS.map((g) => {
                            const count = groupCounts[g.key];
                            if (!count) return null;
                            const present = g.verdicts.filter((v) => verdictCounts[v] > 0);
                            return (
                                <Tooltip key={g.key} content={present.map(verdictLabel).join(' · ')}>
                                    <button
                                        type="button"
                                        className={`inv2-sig k-${g.key}`}
                                        aria-pressed={g.verdicts.some((v) => anomalyFilter.has(v))}
                                        aria-expanded={expanded.has(g.key)}
                                        onClick={() => clickGroup(g)}
                                    >
                                        <i className={`inv2-dot v2-tone-${GROUP_TONES[g.key]}`} aria-hidden="true" />
                                        {g.label}
                                        <span className="inv2-c">{count.toLocaleString('en-US')}</span>
                                    </button>
                                </Tooltip>
                            );
                        })}
                        {anomalyFilter.size > 0 && (
                            <button type="button" className="v2-clear" onClick={own(handleAnomalyClearSignals)}>
                                {anomalyFilter.size > 1 ? `Clear anomaly signals (${anomalyFilter.size})` : 'Clear anomaly signals'}
                            </button>
                        )}
                        <span className="inv2-sg-sep" aria-hidden="true" />
                    </>
                )}
                <span className="inv2-sg-label" aria-hidden="true">Opportunities</span>
                <Tooltip
                    content={filters.qualifyingQuote
                        ? activeText
                        : `${qqCount.toLocaleString('en-US')} units have an active quote at or above the AI estimated value. The buyer is already offering fair market price — call them before dropping the list price.`}
                >
                    <button
                        type="button"
                        className="inv2-sig k-opp"
                        aria-pressed={Boolean(filters.qualifyingQuote)}
                        onClick={toggleQualifyingQuote}
                    >
                        <BoltIcon />
                        Live Quote ≥ AI Price
                        <span className="inv2-c">{qqCount.toLocaleString('en-US')}</span>
                    </button>
                </Tooltip>
                <Tooltip
                    content={filters.retargeting
                        ? activeText
                        : `${retargetCount.toLocaleString('en-US')} units have an active buyer quote within 10% of asking price, or already at/above AI estimated value. Call these buyers before reducing the listed price.`}
                >
                    <button
                        type="button"
                        className="inv2-sig k-opp"
                        aria-pressed={Boolean(filters.retargeting)}
                        onClick={toggleRetargeting}
                    >
                        <TargetIcon />
                        Retarget
                        <span className="inv2-c">{retargetCount.toLocaleString('en-US')}</span>
                    </button>
                </Tooltip>
                {openTaskCount > 0 && (
                    <button
                        type="button"
                        className="inv2-sig k-task"
                        aria-pressed={taskFilterActive}
                        onClick={toggleTaskFilter}
                    >
                        <TaskIcon />
                        Open Tasks
                        <span className="inv2-c">{openTaskCount.toLocaleString('en-US')}</span>
                    </button>
                )}
            </div>

            {ANOMALY_GROUPS.map((g) => {
                if (!expanded.has(g.key)) return null;
                const visible = g.verdicts.filter((v) => verdictCounts[v] > 0);
                if (!visible.length) return null;
                const tone = GROUP_TONES[g.key];
                return (
                    <div key={g.key} className="inv2-verdicts" role="group" aria-label={`Refine ${g.label}`}>
                        <span className="inv2-vg" data-tone={tone}>{`${g.label} ·`}</span>
                        <span className="inv2-vh">Refine signal</span>
                        {visible.map((verdict) => {
                            const cfg = VERDICT_CONFIG[verdict];
                            const label = verdictLabel(verdict);
                            return (
                                <span key={verdict} className="inv2-vchip-wrap">
                                    <button
                                        type="button"
                                        className={`inv2-vchip k-${g.key}`}
                                        data-tone={tone}
                                        aria-pressed={anomalyFilter.has(verdict)}
                                        onClick={own(() => handleAnomalyVerdictToggle(verdict))}
                                    >
                                        <i className={`inv2-dot v2-tone-${tone}`} aria-hidden="true" />
                                        {label}
                                        <span className="inv2-c">{verdictCounts[verdict].toLocaleString('en-US')}</span>
                                    </button>
                                    {cfg && (
                                        <Tooltip
                                            label={`About ${label}`}
                                            content={(
                                                <>
                                                    <strong className="inv2-tip-title">{verdictTooltip(verdict, thresholds)[0]}</strong>
                                                    {verdictTooltip(verdict, thresholds).slice(1).map((line) => (
                                                        <span key={line} className="inv2-tip-line">{line}</span>
                                                    ))}
                                                </>
                                            )}
                                        />
                                    )}
                                </span>
                            );
                        })}
                    </div>
                );
            })}
        </>
    );
}

SignalsRow.propTypes = {
    page: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    allData: PropTypes.arrayOf(PropTypes.object).isRequired,
    shownCount: PropTypes.number.isRequired,
};

export default SignalsRow;
